import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import FilterContainer from "./FilterContainer";
import useDataContext from "../../context/useDataContext";
import useCore from "../../methods/core/useCore";
import { DebounceInput } from "react-debounce-input";
import { checkCurrency } from "../../methods/utilities/checkCurrency";
import useSettingsContext from "../../context/useSettingsContext";

function FilterRange(props) {
    const { minPriceArr, maxPriceArr } = useSettingsContext();

    const { taxSlug, label, scrollable, collapsible } = props;

    const { filterReset, setFilterReset, globalData } = useDataContext();
    const { replaceSelected } = useCore();
    const filterId = "filter-range";

    FilterRange.propTypes = {
        taxSlug: PropTypes.string,
        label: PropTypes.string,
        scrollable: PropTypes.bool,
        collapsible: PropTypes.bool,
    };

    // Set currency based on user location
    const currentCurrency = checkCurrency(globalData["data_currency"]);

    // Set min and max based on user currency
    const min = minPriceArr[`${currentCurrency.toLowerCase()}`];
    const max = maxPriceArr[[`${currentCurrency.toLowerCase()}`]];

    // Inputs State
    const [minVal, setMinVal] = useState(min);
    const [maxVal, setMaxVal] = useState(max);

    // Rest Api String
    const [combinedString, setCombinedString] = useState(null);

    const updateCombinedString = () => {
        const rangeString = `${minVal},${maxVal},${currentCurrency.toLowerCase()}`;

        setCombinedString(rangeString);
    };

    // Creating the refs for inputs elements
    const minValRef = useRef(null);
    const maxValRef = useRef(null);
    const range = useRef(null);

    // Convert to percentage
    const getPercent = useCallback(
        (value) => {
            return Math.round(((value - min) / (max - min)) * 100);
        },
        [min, max]
    );

    // Block displaying modal on Enter
    function keyHandler(event) {
        if (event.key === "Enter") {
            event.preventDefault();
        }
    }

    // Set width of the range to decrease from the left side
    useEffect(() => {
        if (maxValRef.current) {
            const minPercent = getPercent(minVal);
            const maxPercent = getPercent(+maxValRef.current.value);

            if (range.current) {
                range.current.style.left = `${minPercent}%`;
                range.current.style.width = `${maxPercent - minPercent}%`;
            }
        }
    }, [minVal, getPercent]);

    // Set width of the range to decrease from the right side
    useEffect(() => {
        if (minValRef.current) {
            const minPercent = getPercent(+minValRef.current.value);
            const maxPercent = getPercent(maxVal);

            if (range.current) {
                range.current.style.width = `${maxPercent - minPercent}%`;
            }
        }
    }, [maxVal, getPercent]);

    // Variable to prevent useEffect during first render
    const initialRender = useRef(true);

    // Get min and max values when their state changes
    useEffect(() => {
        if (initialRender.current) {
            return;
        }

        updateCombinedString();
    }, [minVal, maxVal]);

    function resetRange() {
        if (filterReset) {
            setMinVal(min);
            setMaxVal(max);
            setFilterReset(false);
        }
    }

    useEffect(() => {
        setMinVal(min);
        setMaxVal(max);
    }, [globalData]);

    useEffect(() => {
        resetRange();
    }, [filterReset]);

    useEffect(() => {
        if (initialRender.current) {
            initialRender.current = false;
            return;
        }
        replaceSelected(combinedString, taxSlug);
    }, [combinedString]);

    const rangeFilter = (
        <div className={currentCurrency + " filter-range-box"}>
            <div className="filter-range-top">
                <DebounceInput
                    type="range"
                    min={min}
                    max={max}
                    debounceTimeout={300}
                    className={
                        "range-input min" +
                        (minVal > max - 7
                            ? " filter-index-5"
                            : " filter-index-3")
                    }
                    value={minVal}
                    ref={minValRef}
                    onChange={(event) => {
                        const value = Math.min(+event.target.value, maxVal - 1);
                        setMinVal(value);
                    }}
                />
                <DebounceInput
                    type="range"
                    min={min}
                    max={max}
                    className="range-input max filter-index-4"
                    value={maxVal}
                    debounceTimeout={300}
                    ref={maxValRef}
                    onChange={(event) => {
                        const value = Math.max(+event.target.value, minVal + 1);
                        setMaxVal(value);
                    }}
                />
                <div className="slider">
                    <div className="slider__track" />
                    <div ref={range} className="slider__range" />
                </div>
            </div>

            <div className="number-inputs">
                <div className="number-input min">
                    <label
                        className="number-input-label"
                        htmlFor="number-input-min"
                    >
                        Min Price
                    </label>
                    <div className="number-input-container">
                        <DebounceInput
                            className="number-input-body min"
                            id="number-input-min"
                            type="number"
                            min={min}
                            max={max - 1}
                            debounceTimeout={500}
                            value={minVal}
                            onChange={(event) => {
                                if (+event.target.value < min) {
                                    event.target.value = min.toString();
                                }
                                const value = Math.min(
                                    +event.target.value,
                                    maxVal - 1
                                );
                                setMinVal(value);
                            }}
                            onKeyPress={(event) => {
                                keyHandler(event);
                            }}
                        />
                    </div>
                </div>
                <div className="number-input max">
                    <label
                        className="number-input-label"
                        htmlFor="number-input-max"
                    >
                        Max Price
                    </label>
                    <div className="number-input-container">
                        <DebounceInput
                            className="number-input-body max"
                            id="number-input-max"
                            type="number"
                            min={minVal + 1}
                            max={max}
                            debounceTimeout={500}
                            value={maxVal}
                            onChange={(event) => {
                                if (+event.target.value > max) {
                                    event.target.value = max.toString();
                                }
                                const value = Math.max(
                                    +event.target.value,
                                    minVal + 1
                                );
                                setMaxVal(value);
                            }}
                            onKeyPress={(event) => {
                                keyHandler(event);
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <FilterContainer
            filterId={filterId}
            className="filter-range"
            label={label}
            collapsible={collapsible}
            scrollable={scrollable}
        >
            {rangeFilter}
        </FilterContainer>
    );
}

export default FilterRange;
